import React, { Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { NuqsAdapter } from "nuqs/adapters/react-router/v6";
import { AppProviders } from "../providers/AppProviders";
import { RootLayout } from "./RootLayout";
import Menu from "../../pages/Menu/Menu";
import Tasks from "../../pages/Tasks/Tasks";
import { SettingsClient } from "../../pages/SettingsClient/SettingsClient";
import Auth from "../../pages/Auth/Auth";
import { AboutSystem } from "../../pages/AboutSystem/AboutSystem";
import { ThemeSettings } from "../../pages/ThemeSettings/ThemeSettings";
import { WsInplantCard } from "../../businessModules/WorkShop/modules/WsInplants/modules/Card/WsInplantCard";
import { WsInplantsList } from "../../businessModules/WorkShop/modules/WsInplants/modules/SearchList/WsInplantsList";
import WsDepotCardsList from "../../businessModules/WorkShop/modules/WsDepotCards/modules/SearchList/WsDepotCardsList";
import { WsInvActsList } from "../../businessModules/WorkShop/modules/WsInvActs/modules/SearchList/WsInvActsList";
import { WsLimitCardsList } from "../../businessModules/WorkShop/modules/WsLimitCards/modules/SearchList/WsLimitCardsList";
import WsDepotCard from "../../businessModules/WorkShop/modules/WsDepotCards/modules/InformationCardMove/WsDepotCard";
import { WsLimitCard } from "../../businessModules/WorkShop/modules/WsLimitCards/modules/LimitCard/WsLimitCard";
import { StockPlacementList } from "../../businessModules/Stock/modules/StockPlacement/modules/SearchList/StockPlacementList";
import { StockPlacementCard } from "../../businessModules/Stock/modules/StockPlacement/modules/Card/StockPlacementCard";
import { StockKanbanSupplyList } from "../../businessModules/Stock/modules/StockKanbanSupply/modules/SearchList/StockKanbanSupplyList";
import { StockKanbanSupplyCard } from "../../businessModules/Stock/modules/StockKanbanSupply/modules/Card/StockKanbanSupplyCard";
import { StockGatheringCompletionList } from "../../businessModules/Stock/modules/StockGatheringCompletion/modules/SearchList/StockGatheringCompletionList";
import { StockGatheringCompletionCard } from "../../businessModules/Stock/modules/StockGatheringCompletion/modules/Card/StockGatheringCompletionCard";
import { StockFinishedGoodsReceiptList } from "../../businessModules/Stock/modules/StockFinishedGoodsReceipt/modules/SearchList/StockFinishedGoodsReceiptList";
import { StockFinishedGoodsReceiptCard } from "../../businessModules/Stock/modules/StockFinishedGoodsReceipt/modules/Card/StockFinishedGoodsReceiptCard";
import { StockFinishedGoodsShipmentList } from "../../businessModules/Stock/modules/StockFinishedGoodsShipment/modules/SearchList/StockFinishedGoodsShipmentList";
import { StockFinishedGoodsShipmentCard } from "../../businessModules/Stock/modules/StockFinishedGoodsShipment/modules/Card/StockFinishedGoodsShipmentCard";
import { StockFullInventoryList } from "../../businessModules/Stock/modules/StockFullInventory/modules/SearchList/StockFullInventoryList";
import { StockFullInventoryCard } from "../../businessModules/Stock/modules/StockFullInventory/modules/Card/StockFullInventoryCard";
import { StockPartialInventoryList } from "../../businessModules/Stock/modules/StockPartialInventory/modules/SearchList/StockPartialInventoryList";
import { StockPartialInventoryCard } from "../../businessModules/Stock/modules/StockPartialInventory/modules/Card/StockPartialInventoryCard";
import NotFound from "../../pages/NotFound/NotFound";
import WsInvActCard from "../../businessModules/WorkShop/modules/WsInvActs/modules/WsInvActCard/WsInvActCard";
import PrivateRoute from "./PrivateRoute";
import { NavigationBridge } from "./NavigationBridge";
import { AppPaths } from "./paths";
/**
 * Единое дерево приложения: BrowserRouter → AppProviders → Routes → MobileAppShell.
 *
 * `BrowserRouter` снаружи `AppProviders`, чтобы `OverlayHost` (sibling routes внутри
 * `OverlayProvider`) тоже был в Router context — иначе `useNavigate` в overlay-окнах падает.
 *
 * `future` — opt-in к поведению React Router v7 (`startTransition`, relative splat paths),
 * чтобы убрать deprecation warnings в v6 и упростить будущую миграцию.
 */
/**
 * Базовый путь приложения из Vite `base` (сборка `--base=/mobile/` для деплоя под саброутом).
 * При base = "/" остаётся дефолтный корень.
 */
const normalizedBaseUrl = import.meta.env.BASE_URL.replace(/\/+$/, "");
const ROUTER_BASENAME = normalizedBaseUrl === "" ? "/" : normalizedBaseUrl;

export const AppRouter = () => (
    <BrowserRouter basename={ROUTER_BASENAME} future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AppProviders>
            <NuqsAdapter>
                <NavigationBridge />
                <Suspense fallback={<></>}>
                    <Routes>
                        <Route path={AppPaths.AUTH} element={<Auth />} />
                        <Route path={AppPaths.SETTING} element={<SettingsClient />} />
                        <Route path={AppPaths.ROOT} element={<RootLayout />}>
                            <Route index element={<Navigate to={AppPaths.MENU} replace />} />
                            <Route path={AppPaths.ABOUT_SYSTEM} element={<AboutSystem />} />
                            <Route path={AppPaths.THEME} element={<ThemeSettings />} />
                            <Route element={<PrivateRoute />}>
                                <Route path={AppPaths.MENU} element={<Menu />} />
                                <Route path={AppPaths.TASKS} element={<Tasks />} />
                                <Route path={AppPaths.WORKSHOP.INPLANTS.LIST_TEMPLATE} element={<WsInplantsList />} />
                                <Route path={AppPaths.WORKSHOP.INV_ACTS.LIST} element={<WsInvActsList />} />
                                <Route path={AppPaths.WORKSHOP.INPLANTS.CARD_TEMPLATE} element={<WsInplantCard />} />
                                <Route path={AppPaths.WORKSHOP.LIMIT_CARDS.LIST} element={<WsLimitCardsList />} />
                                <Route path={AppPaths.WORKSHOP.LIMIT_CARDS.CARD_TEMPLATE} element={<WsLimitCard />} />
                                <Route path={AppPaths.WORKSHOP.DEPOT.LIST} element={<WsDepotCardsList />} />
                                <Route path={AppPaths.WORKSHOP.DEPOT.CARD_TEMPLATE} element={<WsDepotCard />} />
                                <Route path={AppPaths.WORKSHOP.INV_ACTS.CARD_TEMPLATE} element={<WsInvActCard />} />
                                <Route path={AppPaths.STOCK.PLACEMENT.LIST} element={<StockPlacementList />} />
                                <Route path={AppPaths.STOCK.PLACEMENT.CARD_TEMPLATE} element={<StockPlacementCard />} />
                                <Route path={AppPaths.STOCK.KANBAN_SUPPLY.LIST} element={<StockKanbanSupplyList />} />
                                <Route
                                    path={AppPaths.STOCK.KANBAN_SUPPLY.CARD_TEMPLATE}
                                    element={<StockKanbanSupplyCard />}
                                />
                                <Route path={AppPaths.STOCK.PICKING_LISTS.LIST} element={<StockGatheringCompletionList />} />
                                <Route
                                    path={AppPaths.STOCK.PICKING_LISTS.CARD_TEMPLATE}
                                    element={<StockGatheringCompletionCard />}
                                />
                                <Route path={AppPaths.STOCK.FG_RECEIPT.LIST} element={<StockFinishedGoodsReceiptList />} />
                                <Route
                                    path={AppPaths.STOCK.FG_RECEIPT.CARD_TEMPLATE}
                                    element={<StockFinishedGoodsReceiptCard />}
                                />
                                <Route path={AppPaths.STOCK.FG_SHIPMENT.LIST} element={<StockFinishedGoodsShipmentList />} />
                                <Route
                                    path={AppPaths.STOCK.FG_SHIPMENT.CARD_TEMPLATE}
                                    element={<StockFinishedGoodsShipmentCard />}
                                />
                                <Route path={AppPaths.STOCK.FULL_INVENTORY.LIST} element={<StockFullInventoryList />} />
                                <Route
                                    path={AppPaths.STOCK.FULL_INVENTORY.CARD_TEMPLATE}
                                    element={<StockFullInventoryCard />}
                                />
                                <Route
                                    path={AppPaths.STOCK.PARTIAL_INVENTORY.LIST}
                                    element={<StockPartialInventoryList />}
                                />
                                <Route
                                    path={AppPaths.STOCK.PARTIAL_INVENTORY.CARD_TEMPLATE}
                                    element={<StockPartialInventoryCard />}
                                />
                            </Route>
                            <Route path={AppPaths.NOT_FOUND} element={<NotFound />} />
                        </Route>
                    </Routes>
                </Suspense>
            </NuqsAdapter>
        </AppProviders>
    </BrowserRouter>
);
